/* eslint-disable jsx-a11y/no-static-element-interactions -- onClick handlers on div wrappers for event propagation control */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Button, DatePicker, Drawer, Table, Empty, Tooltip } from 'antd-v5';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StandardLayoutV2 from '../../../../Layout/StandardLayoutV2';
import ReportFilter from '../../../../CustomizedAntDesign/ReportFilter';
import ReportTagsFilters from '../../../../CustomizedAntDesign/ReportTagsFilters';
import { getRequest, postRequest } from '../../../../../core/apiRequests';
import { notificationSuccess } from '../../../../../core/actions';
import ConversationWindow from '../../../../../components/Layout/chat/chat';
import {
  getClientChat,
  resetMessaging,
  setChatReviewLink,
  setClientAssociate,
  setMsgReminderId,
  setMsgReminderDescription,
} from '../../../../../reducers/messaging';
import { resetToday } from '../../../../../reducers/today';
import useSessionState from '../../../../../hooks/useSessionState';
import getClientPaymentStatus from '../../../../../components/Layout/GetClientPaymentStatus';
import { associateNameFormat } from '../../../../../utils';
import { listReminders, normalizeReminder } from '../../../../../services/reminders';
import { sourceCalcCentral } from '../../../../../components/Routes/Today/children/logicFunctions';
import SnoozeModal from '../../../../../components/Routes/Today/Reminders/children/SnoozeModal';
import ReminderModal from '../../../../../components/Layout/Modals/EditReminderModal';
import PopConfirm from '../../../../../components/Layout/PopConfirm';
import Modal from '../../../../../components/CustomizedAntDesign/Modal/Modal';
import DropDownSelect from '../../../../../components/Layout/DropdownSelect';
import MessageConfigToday from '../../../../../components/Routes/Today/children/MessageConfigToday';
import '../../../../../components/Routes/Today/today.css';
import {
  DEFAULT_FILTERS,
  DEFAULT_SORT,
  SORTABLE_COLUMNS,
  SOURCE_OPTIONS,
  STATUS_OPTIONS,
  filtersFromSelection,
  flipSort,
  getColumnSortOrder,
  resolveDueDateBounds,
  statusFromFilters,
  toApiSort,
  toDayjsRange,
} from './helpers';

const { RangePicker } = DatePicker;

const AllReminders = ({ auth }) => {
  // Filters persist for the tab so they survive drilling into a client and coming back
  const storageKey = field => (auth.userId ? `all-reminders:${auth.userId}:${field}` : null);
  const [showChat, setShowChat] = useState(false);
  const [pageSize, setPageSize] = useSessionState(storageKey('pageSize'), 25);
  const [page, setPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isShown, setIsShown] = useState('');
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const [editReminderModal, setEditReminderModal] = useState(false);
  const [checkCustomReminder, setCheckCustomReminder] = useState(false);
  const [assignPopup, setAssignPopup] = useState(false);
  const [dismissPopup, setDismissPopup] = useState(false);
  const [dismissAllPopup, setDismissAllPopup] = useState(false);
  const [completePopup, setCompletePopup] = useState(false);
  const [hasOverdueReminders, setHasOverdueReminders] = useState(false);
  const [associate, setAssociate] = useState();
  const [allAssoc, setAllAssoc] = useState([]);

  // Sort state
  const [sortBy, setSortBy] = useSessionState(storageKey('sortBy'), DEFAULT_SORT.sortBy);
  const [sortOrder, setSortOrder] = useSessionState(storageKey('sortOrder'), DEFAULT_SORT.sortOrder);

  // Filter state
  const [filters, setFilters] = useSessionState(storageKey('filters'), DEFAULT_FILTERS);
  const { associateIds: selectedAssociateIds, sources: selectedSources } = filters;
  const selectedStatus = statusFromFilters(filters.statuses);
  const [dueDateRange, setDueDateRange] = useSessionState(storageKey('dueDateRange'), null, {
    deserialize: toDayjsRange,
  });

  useEffect(() => {
    if (selectedRows.length === 1) {
      const [action] = selectedRows;
      setCheckCustomReminder(sourceCalcCentral(action) === 'Manual Reminder');
    } else {
      setCheckCustomReminder(false);
    }
  }, [selectedRows]);

  // Fetch associates for reassignment
  useEffect(() => {
    if (auth.merchantId) {
      const fetchAssociates = async () => {
        const reqObj = {
          params: ['report', auth.merchantId, auth.storeId || 0, 'clientByAction'],
          query: {
            userId: auth.userId,
            role: auth.role,
          },
        };
        const data = await getRequest(reqObj);
        const sortedAssocs = data?.sort((a, b) => {
          const nameA = `${a.first_name || ''}${a.last_name || ''}`;
          const nameB = `${b.first_name || ''}${b.last_name || ''}`;
          return nameA.localeCompare(nameB);
        });
        setAllAssoc(sortedAssocs || []);
        setAssociate(auth.userId);
      };
      fetchAssociates();
    }
  }, [auth.merchantId, auth.storeId, auth.userId, auth.role]);

  const { data: associates } = useQuery(
    ['associates-list'],
    async () => {
      const reqObj = {
        params: ['associates'],
        loadingStatusOff: true,
      };
      return getRequest(reqObj);
    },
    {
      enabled: !!auth.merchantId && auth.role !== 'ASSOCIATE',
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const dueDateBounds = resolveDueDateBounds(dueDateRange, selectedStatus);

  const {
    data: reminders,
    isFetching,
    refetch,
  } = useQuery(
    [
      'allReminders',
      page,
      pageSize,
      selectedAssociateIds,
      selectedSources,
      dueDateBounds.fromDueDate,
      dueDateBounds.toDueDate,
      sortBy,
      sortOrder,
    ],
    async () => {
      try {
        const query = {
          limit: pageSize,
          offset: (page - 1) * pageSize,
          statuses: ['active'],
          associateIds:
            auth.role !== 'ASSOCIATE' && selectedAssociateIds?.length ? selectedAssociateIds : [auth.userId],
          ...(selectedSources?.length ? { sources: selectedSources } : {}),
          ...dueDateBounds,
          ...toApiSort(sortBy, sortOrder),
        };
        const response = await listReminders(query);
        if (response?.data) {
          return {
            data: response.data.map(normalizeReminder),
            total: response.total || response.data.length,
          };
        }
        return { data: [], total: 0 };
      } catch (error) {
        console.error('Error fetching reminders:', error);
        return { data: [], total: 0 };
      }
    },
    {
      refetchOnWindowFocus: false,
      initialData: { data: [], total: 0 },
      enabled: !!auth?.merchantId,
    },
  );

  // Check if there are any overdue reminders
  useEffect(() => {
    if (reminders?.data?.length > 0) {
      const now = moment();
      const hasOverdue = reminders.data.some(
        reminder => !reminder.completeDate && moment(now).isAfter(moment(reminder.dueDate).add(1, 'day')),
      );
      setHasOverdueReminders(hasOverdue);
    } else {
      setHasOverdueReminders(false);
    }
  }, [reminders?.data]);

  const expiresOn = expiresOnDate => {
    return moment(expiresOnDate).diff(moment().startOf('day'), 'days');
  };

  const closeChat = () => {
    setShowChat(false);
    resetMessaging();
    resetToday();
  };

  const getReviewLink = async reviewStoreId => {
    if (reviewStoreId) {
      const reqObj = { params: ['chats', reviewStoreId, 'getReviewLink'] };
      const linkData = await getRequest(reqObj);
      if (linkData.length > 0) {
        setChatReviewLink(linkData[0].review_link);
      }
    }
  };

  const setSelectedClient = async client => {
    await getClientChat(client);
    await getReviewLink(client.store_id);
    setClientAssociate(client.associateCount);
    getClientPaymentStatus({ client, auth });
    setShowChat(true);
    setMsgReminderId(client.id);
    setMsgReminderDescription(client.description);
  };

  const saveSelected = async () => {
    const reqObj = {
      params: ['today', auth.merchantId, 'save'],
      data: { selected: selectedRows },
      query: {
        storeId: auth.storeId,
        associateId: auth.userId,
        createdBy: `${auth.firstName || ''} ${auth.lastName || ''}`,
      },
    };
    const res = await postRequest(reqObj);
    if (res) {
      setSelectedRows([]);
      setSelectedRowKeys([]);
      refetch();
      notificationSuccess('Reminder(s) Complete!');
    }
  };

  const handleClickDismiss = () => {
    const hasRepeating = selectedRows.some(item => item.repeats);
    if (hasRepeating) {
      setCompletePopup(true);
    } else {
      setDismissPopup(true);
    }
  };

  const assignAssociate = async () => {
    setAssignPopup(false);
    const reqObj = {
      params: ['today', auth.merchantId, 'assignAssociate'],
      data: { selectedAction: selectedRows },
      query: {
        assignedAssociate: associate,
        storeId: auth.storeId,
        associateId: auth.userId,
      },
    };
    const res = await postRequest(reqObj);
    if (res) {
      setSelectedRows([]);
      setSelectedRowKeys([]);
      refetch();
      notificationSuccess('Reminder(s) Assigned!');
      setAssociate(auth.userId);
    }
  };

  const dismissSelected = async (deleteAll = false) => {
    const reqObj = {
      params: ['today', auth.merchantId, 'dismissSelected'],
      query: {
        associateName: associateNameFormat(auth.firstName, auth.lastName),
        associateId: auth.userId,
      },
      data: {
        selectedAction: selectedRows.map(item => item.id),
        repeats: deleteAll ? 0 : 1,
      },
    };
    const responseObj = await postRequest(reqObj);
    if (responseObj) {
      setSelectedRows([]);
      setSelectedRowKeys([]);
      setDismissPopup(false);
      setCompletePopup(false);
      refetch();
      notificationSuccess(`Reminder(s) ${deleteAll ? 'Deleted' : 'Dismissed'}!`);
    }
  };

  const dismissAllOverdue = async () => {
    const reqObj = {
      params: ['today', auth.merchantId, 'dismissAll'],
      data: {
        associateName: associateNameFormat(auth.firstName, auth.lastName),
        associateId: auth.userId,
      },
    };
    const responseObj = await postRequest(reqObj);
    if (responseObj) {
      setDismissAllPopup(false);
      refetch();
      notificationSuccess('Reminder(s) Dismissed!');
    }
  };

  const filterOptions = useMemo(
    () => [
      ...(auth.role !== 'ASSOCIATE'
        ? [
            {
              title: 'Associate',
              key: '0-1',
              value: 'associateIds',
              children: (associates || []).map(a => ({
                title: `${a.first_name || ''} ${a.last_name || ''}`.trim(),
                value: String(a.id),
                key: `0-1-${a.id}`,
              })),
            },
          ]
        : []),
      {
        title: 'Source',
        key: '0-2',
        value: 'sources',
        children: SOURCE_OPTIONS.map(option => ({
          title: option.label,
          value: option.value,
          key: `0-2-${option.value}`,
        })),
      },
      {
        title: 'Status',
        key: '0-3',
        value: 'statuses',
        children: STATUS_OPTIONS.map(option => ({
          title: option.label,
          value: option.value,
          key: `0-3-${option.value}`,
        })),
      },
    ],
    [associates, auth.role],
  );

  const handleFilterChange = useCallback(selection => {
    setFilters(filtersFromSelection(selection));
    setPage(1);
  }, []);

  const removeFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: prev[key].filter(item => item !== value) }));
    setPage(1);
  }, []);

  const hasActiveFilters = Boolean(Object.values(filters).some(values => values.length > 0) || dueDateRange);

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setDueDateRange(null);
    setPage(1);
  };

  const handleTableChange = useCallback((pagination, _filters, sorter, extra) => {
    if (extra.action === 'sort') {
      const order = Array.isArray(sorter) ? sorter[0]?.order : sorter?.order;
      const columnKey = Array.isArray(sorter) ? sorter[0]?.columnKey : sorter?.columnKey;
      if (order) {
        setSortBy(SORTABLE_COLUMNS.includes(columnKey) ? columnKey : DEFAULT_SORT.sortBy);
        setSortOrder(order === 'ascend' ? 'ASC' : 'DESC');
      } else {
        setSortOrder(flipSort);
      }
      setPage(1);
    } else if (extra.action === 'paginate') {
      setPage(pagination.current);
      setPageSize(pagination.pageSize);
    }
  }, []);

  const rowSelection = {
    selectedRowKeys,
    columnWidth: 48,
    onChange: (newSelectedRowKeys, newSelectedRows) => {
      setSelectedRowKeys(newSelectedRowKeys);
      setSelectedRows(newSelectedRows);
    },
  };

  const columns = useMemo(
    () => [
      {
        title: 'Due Date',
        dataIndex: 'dueDate',
        key: 'dueDate',
        width: '15%',
        sorter: true,
        sortOrder: getColumnSortOrder('dueDate', sortBy, sortOrder),
        render: (t, record) => {
          if (t) {
            const formatted = moment(t).format('MM/DD/YYYY');
            if (record.expiresOnDate) {
              const daysLeft = expiresOn(record.expiresOnDate);
              if (daysLeft <= 0) {
                return (
                  <div className="flex-col-left">
                    {formatted}
                    <span className="fs-12 fw-400 gray">Expires today</span>
                  </div>
                );
              }
              if (daysLeft === 1) {
                return (
                  <div className="flex-col-left">
                    {formatted}
                    <span className="fs-12 fw-400 gray">Expires tomorrow</span>
                  </div>
                );
              }
              return (
                <div className="flex-col-left">
                  {formatted}
                  <span className="fs-12 fw-400 gray">Expires in {daysLeft} days</span>
                </div>
              );
            }
            return formatted;
          }
          return null;
        },
      },
      {
        title: 'Client Name',
        key: 'clientName',
        width: '15%',
        sorter: true,
        sortOrder: getColumnSortOrder('clientName', sortBy, sortOrder),
        render: (_, record) => (
          <Link to={`/Clients?client=${record.client?.id}`} onClick={e => e.stopPropagation()}>
            {record.client?.name}
          </Link>
        ),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        width: '25%',
        ellipsis: { showTitle: false },
        render: description => (
          <Tooltip title={description} placement="topLeft">
            {description}
          </Tooltip>
        ),
      },
      {
        title: 'Source',
        key: 'source',
        width: '12%',
        sorter: true,
        sortOrder: getColumnSortOrder('source', sortBy, sortOrder),
        render: (_, record) => sourceCalcCentral(record),
      },
      {
        title: 'Reassign',
        dataIndex: 'reassigned_by',
        key: 'reassigned_by',
        width: '8%',
        align: 'center',
        render: () => (
          <div className="align-center" style={{ maxHeight: '34px' }}>
            <FontAwesomeIcon
              icon={['far', 'pencil']}
              onClick={e => {
                e.stopPropagation();
                setAssignPopup(true);
              }}
              className="pointer gray"
            />
          </div>
        ),
      },
      {
        title: 'Message',
        dataIndex: 'message',
        key: 'message',
        width: '10%',
        align: 'center',
        render: (_, record) => (
          <div className="align-center pos-rel" style={{ maxHeight: '34px' }} onClick={e => e.stopPropagation()}>
            <MessageConfigToday
              keyVar={Math.floor(Math.random() * record.id)}
              msgObj={record}
              setShowPopConfirm={setSelectedClient}
              initiateTemplates={setSelectedClient}
              setIsShown={setIsShown}
              isShown={isShown}
            />
          </div>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: '12%',
        align: 'center',
        sorter: true,
        sortOrder: getColumnSortOrder('status', sortBy, sortOrder),
        render: (_, record) => {
          if (!record.completeDate) {
            if (moment().isAfter(moment(record.dueDate).add(1, 'day'))) {
              return <span className="today-status-badge overdue">OVERDUE</span>;
            }
            return <span className="today-status-badge active">ACTIVE</span>;
          }
          return null;
        },
      },
    ],
    [isShown, sortBy, sortOrder],
  );

  return (
    <StandardLayoutV2
      title="Reminders"
      breadcrumb={[{ path: '/', title: 'Today' }, { title: 'Reminders' }]}
      classProps="flex-col-left"
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent', paddingTop: '0px' }}
      actions={
        selectedRowKeys.length > 0 ? (
          <div className="cb-link-btn-group cb-link-btn-group--wide">
            {checkCustomReminder && (
              <Button type="link" className="cb-link-btn-action" onClick={() => setEditReminderModal(true)}>
                <FontAwesomeIcon icon={['far', 'pencil']} />
                Edit
              </Button>
            )}
            <Button type="link" className="cb-link-btn-action" onClick={() => setAssignPopup(true)}>
              <FontAwesomeIcon icon={['far', 'user']} />
              Assign
            </Button>
            <Button type="link" className="cb-link-btn-action cb-link-btn-action--icon-lg" onClick={saveSelected}>
              <FontAwesomeIcon icon={['far', 'check-circle']} />
              Complete
            </Button>
            <Button type="link" className="cb-link-btn-action" onClick={handleClickDismiss}>
              <FontAwesomeIcon icon={['far', 'eraser']} />
              Dismiss
            </Button>
            <Button type="link" className="cb-link-btn-action" onClick={() => setShowSnoozeModal(true)}>
              <FontAwesomeIcon icon={['far', 'alarm-clock']} />
              Snooze
            </Button>
          </div>
        ) : (
          <div className="cb-link-btn-group">
            <Button
              type="link"
              className="cb-link-btn-action cb-link-btn-action--icon-lg"
              onClick={() => setDismissAllPopup(true)}
              disabled={!hasOverdueReminders}
            >
              <FontAwesomeIcon icon={['far', 'eraser']} />
              Dismiss All Overdue
            </Button>
          </div>
        )
      }
    >
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <RangePicker
          value={dueDateRange}
          onChange={dates => {
            setDueDateRange(dates);
            setPage(1);
          }}
          placeholder={['Due From', 'Due To']}
          format="MM/DD/YYYY"
        />
        <ReportFilter options={filterOptions} handleFilterChange={handleFilterChange} filters={filters} />
        {hasActiveFilters && (
          <Button type="link" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </div>
      <ReportTagsFilters options={filterOptions} filterSelected={filters} removeFilter={removeFilter} />
      <Table
        className="today-table"
        style={{ marginTop: 16 }}
        columns={columns}
        dataSource={reminders?.data || []}
        rowKey={record => record.id}
        loading={isFetching}
        rowSelection={rowSelection}
        onRow={() => ({ 'data-testid': 'reminders__table--row' })}
        onChange={handleTableChange}
        pagination={{
          pageSize,
          current: page,
          total: reminders?.total || 0,
          showSizeChanger: { showSearch: false },
          showTotal: t => `Total ${t} items`,
        }}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Reminders" />,
        }}
      />

      {editReminderModal && (
        <ReminderModal
          open={editReminderModal}
          reminder={selectedRows?.[0] ?? {}}
          allAssoc={allAssoc}
          onClose={() => setEditReminderModal(false)}
          setReload={() => {
            setSelectedRows([]);
            setSelectedRowKeys([]);
            refetch();
          }}
        />
      )}

      <SnoozeModal
        openModal={showSnoozeModal}
        setOpenModal={setShowSnoozeModal}
        merchantId={auth.merchantId}
        actions={selectedRows}
        refresh={() => {
          setSelectedRows([]);
          setSelectedRowKeys([]);
          refetch();
        }}
      />

      {assignPopup && (
        <PopConfirm
          popStyle={{ width: '380px' }}
          confirmButtonText="Save"
          cancelButtonText="Cancel"
          confirm="Assign Associate"
          description={[
            <div key="primary" className="align-left m-top-10" style={{ height: '175px' }}>
              <span className="dropDownDescriptionText">Assign an Associate</span>
              <DropDownSelect
                classProps="mq-w-200 w-295 align-left m-top-8"
                options={allAssoc.map(asc => ({
                  label: asc.fullName ? asc.fullName : `${asc.first_name || ''} ${asc.last_name || ''}`,
                  value: asc.id,
                }))}
                placeholder="Select an Associate..."
                value={associate}
                onChange={obj => {
                  setAssociate(obj.value);
                }}
              />
            </div>,
          ]}
          noButtonDisable
          onCancel={() => setAssignPopup(false)}
          onConfirm={assignAssociate}
        />
      )}

      {dismissPopup && (
        <PopConfirm
          popStyle={{ width: '380px' }}
          customHeaderContent="fs-16 align-center"
          confirmButtonText="Dismiss"
          cancelButtonText="Cancel"
          confirm="Dismiss Selected Reminders"
          description="These reminders will be dismissed from your list. If you continue, these reminders will be permanently removed from your profile."
          noButtonDisable
          onCancel={() => setDismissPopup(false)}
          onConfirm={dismissSelected}
        />
      )}

      {dismissAllPopup && (
        <PopConfirm
          popStyle={{ width: '380px' }}
          customHeaderContent="fs-16 align-center"
          confirmButtonText="Dismiss"
          cancelButtonText="Cancel"
          confirm="Dismiss All Overdue Reminders"
          description="All overdue reminders will be removed from your list. If you continue, these reminders will be permanently removed from your profile."
          noButtonDisable
          onCancel={() => setDismissAllPopup(false)}
          onConfirm={dismissAllOverdue}
        />
      )}

      <Modal
        open={completePopup}
        onCancel={() => setCompletePopup(false)}
        footer={null}
        width={348}
        destroyOnClose
        closable
        centered
        className="cb-reminders-modal"
      >
        <div className="cb-reminders-modal__body">
          <h3>Dismiss reminders</h3>
          <p>This reminder repeats. Would you like to dismiss this occurrence or delete it?</p>
        </div>
        <div className="flex-row-center m-top-15" style={{ gap: '12px' }}>
          <Button size="large" onClick={() => dismissSelected(false)}>
            Dismiss
          </Button>
          <Button type="primary" size="large" onClick={() => dismissSelected(true)}>
            Delete All
          </Button>
        </div>
      </Modal>

      <Drawer
        styles={{
          body: { padding: 0, overflow: 'hidden' },
        }}
        closable={false}
        push={false}
        destroyOnClose
        width={750}
        open={showChat}
        onClose={closeChat}
      >
        <div style={{ height: '100vh' }}>
          <ConversationWindow setDrawerToggle={closeChat} chatIconColor="#EBEBEB" type="right" />
        </div>
      </Drawer>
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AllReminders);
